import { classnames, toRoute } from '@aibees/shared/utils';
import { Typography } from '@mui/material';
import { invert } from 'lodash-es';
import { memo, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { LinkRouter } from './components/link-router';
import * as Styled from './styles';
import * as SX from './sx';
import type { HeaderBreadcrumbsProps as Props } from './types';
import { ROUTE_HOME } from '@aibees/shared/constants';

const UUID_REGEX = /^[a-f0-9]+-[a-f0-9]+-[a-f0-9]+-[a-f0-9]$/;

export const HeaderBreadcrumbs = memo(
  ({ className, config: { routes, titles } }: Props) => {
    const location = useLocation();
    const routeParams = useParams();
    const pathnames = location.pathname
      .split('/')
      .filter((x) => x)
      .map((x) => (x.match(UUID_REGEX) ? routeParams.id : x));
    const reverseRoutes = useMemo(() => {
      const cloned = { ...routes };
      Object.keys(cloned).forEach((k) => {
        cloned[k] = toRoute(cloned[k], routeParams);
      });
      return invert(cloned);
    }, [routes, routeParams]);
    const labels = useMemo(() => {
      const cloned = { ...titles };
      Object.keys(titles).forEach((k) => {
        cloned[k] = toRoute(cloned[k], routeParams);
      });
      return cloned;
    }, [routeParams, titles]);

    const isHomePage = location.pathname === ROUTE_HOME;

    return (
      <Styled.Wrapper
        className={classnames('HeaderBreadcrumbs', className)}
        sx={SX.root}
      >
        <Styled.LinkRouter underline="hover" color="inherit" to="/">
          Home
        </Styled.LinkRouter>
        {!isHomePage &&
          pathnames.map((value, index: number) => {
            const last = index === pathnames.length - 1;
            const noRoute = value === 'search';
            const to = `/${pathnames.slice(0, index + 1).join('/')}`;
            const labelIndex = reverseRoutes[to];
            if (!labels[labelIndex]) {
              return null;
            }
            return last || noRoute ? (
              <Typography
                component="p"
                sx={
                  last
                    ? {
                        color: (p) => p.palette.common.black,
                        lineHeight: 2,
                        fontWeight: '700 !important',
                        fontSize: '10px !important',
                      }
                    : {
                        lineHeight: 2,
                        fontWeight: '700 !important',
                        fontSize: '10px !important',
                      }
                }
                key={to}
              >
                {labels[labelIndex] || '-'}
              </Typography>
            ) : (
              <LinkRouter
                underline="hover"
                color="inherit"
                to={to}
                key={to}
                sx={{
                  lineHeight: 2,
                  fontWeight: '700 !important',
                  fontSize: '10px !important',
                }}
              >
                {labels[labelIndex] || '-'}
              </LinkRouter>
            );
          })}
      </Styled.Wrapper>
    );
  }
);

HeaderBreadcrumbs.displayName = 'HeaderBreadcrumbs';
